/**
 * Published apps: the catalogue, the ones you own, and the one you are looking at.
 *
 * Web-owned because the shared myAppsStore fetches its token from CognitoService, which
 * since SEC-14 reaches expo-secure-store and therefore React Native.
 */
import type { AppDefinition } from '@/types/app';
import type { AppListing } from '@/backend/types';
import { pickAppIcon } from '@/utils/appIcon';
import { ApiError, request } from './client';

/**
 * Register an app in the catalogue, and answer with the id every other route resolves.
 *
 * An app has TWO ids and they are not interchangeable. The build service mints a document
 * id — it is baked into the page as the localStorage namespace, so it must never change.
 * The catalogue id is this server's own nanoid, and it is what `/apps/{appId}/…` looks up.
 * This client only ever had the first, so publishing posted to `/apps/<document id>/web`
 * and was answered, correctly, with 404: no such app. A finished, paid-for build ended at
 * "We could not find that."
 *
 * Published as `sharable` whatever the project says. The server refuses to web-publish a
 * personal app, and does not even write one to the catalogue partition — so a personal app
 * would 404 on the very next call.
 */
export async function createApp(input: {
  name: string;
  description?: string;
  definition: AppDefinition;
  icon?: string;
  /**
   * Whether the app appears in Discover. Defaults to NO.
   *
   * Nothing in this client offers the choice — there is no listing control in the publish
   * panel and the Discover screen is browse-only — so listing somebody's app is not something
   * they could have asked for.
   */
  listed?: boolean;
}): Promise<string> {
  const listing = await request<{ appId?: string }>('/apps', {
    method: 'POST',
    body: {
      name: input.name,
      description: input.description ?? '',
      icon: input.icon ?? pickAppIcon(input.definition),
      /*
       * `kind: 'sharable'` is forced, and it answers a DIFFERENT question.
       *
       * The published page is served by reading CATALOG, so a personal-kind app would 404 —
       * the kind is what makes the link work. Who gets SHOWN the app is `listed`, and
       * conflating the two is what put everybody's apps in the public gallery.
       */
      definition: { ...input.definition, kind: 'sharable' },
      /*
       * Said out loud, because silence is not neutral: the server reads an ABSENT `listed` as
       * LISTED (`listCatalog` filters `listed !== false`). This field was simply not sent, so
       * every app built on the web went into Discover for strangers to browse.
       */
      listed: input.listed ?? false,
      schemas: [],
    },
  });
  if (!listing.appId) {
    throw new ApiError('server', 'The app was not registered, so there is nowhere to publish it. Try again.');
  }
  return listing.appId;
}

/** Everything published and listed. Unlisted apps never appear here. */
export async function fetchCatalog(): Promise<AppListing[]> {
  const body = await request<{ items?: AppListing[] }>('/apps');
  return body.items ?? [];
}

/** The apps this person owns or has joined. */
export async function fetchMyApps(): Promise<AppListing[]> {
  const body = await request<{ items?: AppListing[] }>('/me/apps');
  return body.items ?? [];
}

/** One app. Null rather than a throw when it simply is not there. */
export async function fetchApp(appId: string): Promise<AppListing | null> {
  try {
    return await request<AppListing>(`/apps/${encodeURIComponent(appId)}`);
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return null;
    throw err;
  }
}

export async function enrollInApp(appId: string): Promise<void> {
  await request(`/apps/${encodeURIComponent(appId)}/enroll`, { method: 'POST' });
}

/**
 * The definition behind a published app, for remixing it.
 *
 * Null when the app has none to give — a remix of nothing should offer nothing, rather
 * than create an empty project the person then has to delete.
 */
export async function fetchPublishedDefinition(appId: string): Promise<AppDefinition | null> {
  try {
    const app = await request<{ definition?: AppDefinition }>(`/apps/${encodeURIComponent(appId)}`);
    return app.definition ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return null;
    throw err;
  }
}
